import { logoSrc } from "../../api/config.ts";
import { Link } from "../../app/router.ts";
import { shortAddr } from "../../lib/format.ts";
import s from "./token.module.css";

/**
 * WHO A TOKEN IS — logo, symbol, name, address — rendered ONE way, everywhere it appears.
 *
 * It lives in `features/token/` and is imported by the board and the wallet page, because a token's identity
 * is the token area's concern and the alternative is three renderings that disagree about the same mint —
 * paid for twice already: a venue that read "Raydium LaunchLab" in one list and a raw program id in another,
 * and a symbol that was truncated differently per screen.
 *
 * ── THE LOGO IS LOADED FROM OUR OWN ORIGIN, NOT FROM THE URL ON THE ROW ────────────────────────────────
 *
 * `src` is `logoSrc(mint)` — `/v1/token/:mint/logo` — and NEVER `image`. MEASURED 2026-08-19: `ipfs.io`,
 * which carries 41% of every row on the launch board, began answering a BROWSER-shaped request with `403` +
 * `cf-mitigated: challenge` while answering our server with `200`. An `<img>` has no JS context and can
 * never solve a challenge, so those logos became unreachable from this page while staying reachable from
 * the API. Relaying them also answers the OTHER failure this file used to document — Referer-keyed hotlink
 * protection on `m.rapidlaunch.io`, measured 200-without/403-with — because our fetch is not a browser's
 * and sends no Referer. One relay, both mechanisms, and no per-host allowlist to go stale.
 *
 * `image` stays on the row and stays meaningful: it is the upstream provenance, it feeds the `uri`-clone
 * mark, and `image !== null` is exactly the test for whether a logo EXISTS — which is why the relay needed
 * no new wire field.
 *
 * ── THE LOGO IS A MONOGRAM WITH AN IMAGE ON TOP, AND THAT IS STILL DELIBERATE ──────────────────────────
 *
 * The relay removes the two failures above; it does not make a logo certain. A mint we hold no url for, a
 * CDN that has since gone dark, and a relay refusing under load all answer 404 or 503. ⚠️ A CSP of
 * `img-src 'self'` would now be SATISFIED by these loads rather than broken by them — checked 2026-08-19,
 * still none is shipped (neither `deploy/nginx/kinetiq.conf` nor a meta tag), but the relay is what makes
 * one addable at all. So the monogram (first character of the symbol) is painted underneath unconditionally
 * and the image sits over it: a blocked, 404'd or slow image degrades to a readable initial rather than to a
 * hole, with no per-row JavaScript state and no `onError` handler.
 *
 * ── A NAMELESS TOKEN IS A COVERAGE STATEMENT, NOT AN ERROR ─────────────────────────────────────────────
 *
 * `name`/`symbol` are `null` until enrichment has run for that mint. The address is therefore ALWAYS shown
 * beside the symbol rather than only when the symbol is missing — it is the only identifier that is never
 * absent, and it is what a user pastes into an explorer when a symbol looks like an impersonation.
 */
export interface TokenIdentityProps {
  mint: string;
  symbol: string | null;
  name: string | null;
  /** Kept on the props because every caller passes it and it is still the provenance of the url, but the
   *  tag no longer branches on it: see the note beside the <img>. */
  image?: string | null;
  /** Link to the token page. `false` on the token page itself, where the link would point at the page you are
   *  already on. */
  link?: boolean;
}

export function TokenIdentity({ mint, symbol, name, link = true }: TokenIdentityProps) {
  const label = symbol ?? name ?? shortAddr(mint, 4);
  const monogram = (symbol ?? name ?? mint).slice(0, 1).toUpperCase();

  const body = (
    <>
      <span className={s.logo} aria-hidden="true">
        <span className={s.monogram}>{monogram}</span>
        {/* ALWAYS ASKED, NEVER GATED ON `image`. A launch is on screen SECONDS after it is minted and
         * enrichment needs ~90s, so `image` is null for exactly the rows a user is looking at — gating the
         * tag on it meant the relay was never asked for the newest tokens and the launches board showed
         * no logos at all. The relay answers for ANY mint: the bytes when we hold a url, a drawn monogram
         * when we do not, so asking unconditionally is strictly better than asking never. The CSS
         * monogram below stays as the paint-before-load state. */}
        <img
          className={s.logoImg}
          /* NOT `image`. That is the UPSTREAM url and it is on the row for provenance only; the bytes
           * come from our own origin through the relay. */
          src={logoSrc(mint)}
          alt=""
          loading="lazy"
          /* KEPT, THOUGH THE REQUEST IS NOW SAME-ORIGIN AND THE HOST THAT NEEDED IT IS NO LONGER THE ONE
           * BEING ASKED.
           *
           * The measurement it was added for still stands — 2026-08-17, `m.rapidlaunch.io` answers the
           * same URL 200 with no Referer and **403 with `Referer: https://kinetiq.xyz/`** — but that host
           * is now spoken to by the API, not by this tag, and our fetch sends no Referer either. What this
           * attribute buys today is that a redirect off our origin (a future CDN in front of `/v1`) cannot
           * reintroduce the failure, and it costs nothing: these are public logos and we authenticate to
           * nobody. */
          referrerPolicy="no-referrer"
        />
      </span>
      <span className={s.identityText}>
        <span className={s.symbol}>{label}</span>
        {/* The NAME is secondary and the ADDRESS is always present: a symbol can be impersonated, an address
            cannot. */}
        <span className={s.identitySub} title={mint}>
          {name && name !== label ? `${name} · ` : ""}
          {shortAddr(mint, 4)}
        </span>
      </span>
    </>
  );

  return link ? (
    <Link to={`#/token/${mint}`} className={s.identity}>
      {body}
    </Link>
  ) : (
    <span className={s.identity}>{body}</span>
  );
}
